import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { useRadar } from '../api'
import type { Basemap } from '../lib/basemapPref'
import { loadBasemapPref, resolveBasemap } from '../lib/basemapPref'
import { readBootGround, readBootMaterial } from '../lib/bootSky'
import { lazyModule } from '../lib/lazyModule'
import { useReducedMotion } from '../hooks'
import { selectedPlace, useApp } from '../store'
import { FADE_MS, FAILSAFE_MS, introEnd, introStart, mapPictureFor, pictureScale, takeOrigin } from './openPicture'

/**
 * What the map page shows until MapLibre has drawn its first complete frame: the place's sky as the
 * ground and, on top of it, the radar card's picture of the place at the map's initial camera (grown
 * from the card when it was the card that was pressed). Plain DOM and inline styles, so it also
 * serves as the route fallback while the map's code is still arriving — it never loads MapLibre and
 * adds no stylesheet to the first page.
 *
 * The map is not hidden or faded in: it is complete underneath, and this layer fades out (opacity
 * only) to reveal it.
 */
// Held outside React (not React.lazy), so once its chunk is in, the picture is in the same commit as the tap.
const staticRadar = lazyModule(() => import('../home/StaticRadar'))
/** Fetches the picture's drawing code (the forecast page's radar card chunk): called when a tap on the map is likely. */
export const warmStaticRadar = staticRadar.load

const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)'
const GROW_MS = 340

export interface MapIntroProps {
  latitude: number
  longitude: number
  /** CSS background of the place's sky. */
  ground: string | null
  /** MapLibre painted its first complete frame (basemap and the current radar frame). */
  reveal?: boolean
  /** The map failed: keep the picture instead of fading to nothing. */
  hold?: boolean
  /** Route fallback: the map's code is still arriving. No timers, no fade. */
  waiting?: boolean
  /** Draw a fresh picture when the forecast page left none (a deep link, or a card never scrolled to). */
  basemap?: Basemap
  radarTiles?: string | null
}

export default function MapIntro({ latitude, longitude, ground, reveal = false, hold = false, waiting = false, basemap, radarTiles = null }: MapIntroProps) {
  const reduced = useReducedMotion()
  const Radar = staticRadar.use()?.default ?? null
  const [failed, setFailed] = useState(false)
  const [gone, setGone] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  const [picture] = useState(() => mapPictureFor(latitude, longitude))
  const k = picture ? pictureScale(picture.zoom) : pictureScale(6)

  // Failsafe: whatever the map is doing, the picture leaves (unless the map failed or the device is offline,
  // where the picture is the better thing to look at).
  useEffect(() => {
    if (waiting || hold || reveal) return
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return
    const id = window.setTimeout(() => setFailed(true), FAILSAFE_MS)
    return () => window.clearTimeout(id)
  }, [waiting, hold, reveal])

  const out = !waiting && (reveal || (failed && !hold))
  useEffect(() => {
    if (!out) return
    const id = window.setTimeout(() => setGone(true), reduced ? 0 : FADE_MS + 40)
    return () => window.clearTimeout(id)
  }, [out, reduced])
  useEffect(() => {
    if (!waiting) return () => introEnd()
  }, [waiting])

  // Grow from the card to the map's camera. One timeline, shared with the route fallback, so the swap
  // from fallback to page continues the motion instead of restarting it.
  useLayoutEffect(() => {
    const el = wrap.current
    // A picture the route fallback drew fresh is already at the map's camera: growing it from the card's
    // size here would shrink it by a few percent at the swap and swell it back.
    if (!el || !picture || picture.full || reduced || typeof el.animate !== 'function') return
    const o = takeOrigin()
    if (!o) return
    const dx = o.x + o.width / 2 - window.innerWidth / 2
    const dy = o.y + o.height / 2 - window.innerHeight / 2
    const animation = el.animate(
      [
        { transform: `translate(${dx}px, ${dy}px) scale(${o.width / picture.width})` },
        { transform: `translate(0px, 0px) scale(${k})` },
      ],
      { duration: GROW_MS, easing: EASE, fill: 'both' },
    )
    animation.currentTime = Math.max(0, performance.now() - introStart())
    return () => animation.cancel()
  }, [picture, reduced, k])

  // The picture is copied into this component's own canvas (the card's canvas stays where it is).
  const paint = (canvas: HTMLCanvasElement | null) => {
    if (!canvas || !picture) return
    canvas.width = picture.canvas.width
    canvas.height = picture.canvas.height
    canvas.getContext('2d')?.drawImage(picture.canvas, 0, 0)
  }

  if (gone) return null
  const style: CSSProperties = {
    position: waiting ? 'fixed' : 'absolute',
    inset: 0,
    zIndex: waiting ? 5 : 2,
    overflow: 'hidden',
    pointerEvents: 'none',
    background: ground ?? 'linear-gradient(180deg, var(--sky-top, #0b1628), var(--sky-bottom, #0b1628))',
    opacity: out ? 0 : 1,
    transition: out && !reduced ? `opacity ${FADE_MS}ms ${EASE}` : 'none',
  }
  // The same picture drawn fresh, at the size that the map's camera needs once scaled up.
  const fw = Math.ceil((typeof window === 'undefined' ? 1280 : window.innerWidth) / k)
  const fh = Math.ceil((typeof window === 'undefined' ? 800 : window.innerHeight) / k)
  return (
    <div className="mc-intro" data-out={out || undefined} style={style} aria-hidden>
      {picture ? (
        <div
          ref={wrap}
          className="mc-intro-picture"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: picture.width,
            height: picture.height,
            margin: `${-picture.height / 2}px 0 0 ${-picture.width / 2}px`,
            borderRadius: picture.full ? 0 : '1.5rem',
            overflow: 'hidden',
            transform: `scale(${k})`,
            transformOrigin: '50% 50%',
          }}
        >
          <canvas ref={paint} style={{ display: 'block', width: '100%', height: '100%' }} />
        </div>
      ) : basemap ? (
        <div
          className="mc-intro-fresh"
          style={{ position: 'absolute', left: '50%', top: '50%', width: fw, height: fh, margin: `${-fh / 2}px 0 0 ${-fw / 2}px`, transform: `scale(${k})`, transformOrigin: '50% 50%' }}
        >
          {Radar ? <Radar longitude={longitude} latitude={latitude} radarTiles={radarTiles} basemap={basemap} pin={null} intro /> : null}
        </div>
      ) : null}
    </div>
  )
}

/**
 * The route fallback while the map's code arrives: the place's sky and, at once, the card's picture —
 * or, when the card never drew one (a first visit, a deep link, a tap before the card was scrolled to),
 * the same picture drawn fresh from the tiles the tap already asked for (map/warm.ts).
 */
export function MapRouteFallback() {
  const place = useApp((s) => selectedPlace(s))
  const [basemap] = useState(() => resolveBasemap(loadBasemapPref(), readBootMaterial(place.latitude, place.longitude) === 'light'))
  const radar = useRadar(true, place)
  return (
    <MapIntro
      waiting
      latitude={place.latitude}
      longitude={place.longitude}
      ground={readBootGround(place.latitude, place.longitude)}
      basemap={basemap}
      radarTiles={radar.data?.past.at(-1)?.tiles ?? null}
    />
  )
}
